

/* eslint-disable prefer-const */

import dayjs from 'dayjs';
import 'dayjs/locale/nb';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { dateFormat, PAYMENT_METHODS } from 'utils/constants';

// Enable UTC and custom parse format plugins
dayjs.extend(utc);
dayjs.extend(customParseFormat);

dayjs.locale('nb');

/*
{
  'profile_data': profile_data,
  'pickup_data': {
      'prev': prev_pickups,
      'next': next_pickups
  },
  'subscriber_data': {
      'stripe_customer': customer,
      'subscription': subscription,
      'invoices': stripe_invoices,
      'payments': payments,
      'payment_plans': get_payment_plans()
  }
}

*/
export const subscriptionData = (user) => {
  if (!user || !Object.keys(user).length) {
    return {};
  }

  const profileData = user['profile_data'];
  const customerInfo = user['subscriber_data'];

  const subscriptionStart = profileData['subscription_start'];
  const paymentFrequency = profileData['payment_frequency'];
  const paymentMethod = profileData['payment_method'];
  const hasBox = profileData['has_box'];
  const subscriptionState = profileData['subscription_state'];
  const plans = customerInfo['payment_plans'];
  const name = profileData['name'];
  const routeInfo = profileData['route_info'];

  const isStrexCustomer = paymentMethod === PAYMENT_METHODS['STREX'];
  const isStripeCustomer = paymentMethod === PAYMENT_METHODS['STRIPE'];
  const usesInvoice = paymentMethod === PAYMENT_METHODS['INVOICE'];

  const { strexContracts } = customerInfo;

  let subscription = null;
  let activeSubscription = false;
  let pastDue = false;
  let nextPayment = null;
  let oldNextPayment = null;
  let hasCancelled = false;
  let paymentSource = null;
  let delayedStart = null;
  let hasPublicStripePlan = false;
  let hasStripeBox = false;
  let cardExpiring = false;
  let cardExpired = false;
  let cardExpDate = null;
  let card = null;
  let coupon = null;
  let upcomingInvoice = null;
  let nextPaymentAmount = null;
  let planInterval = null;
  let planIntervalCount = null;
  let currentPeriodEnd = null;
  let boxUpgradePlan = null;
  let intervalChangePlan = null;

  // Create a lookup map for database payments to avoid O(n²) complexity
  const savedPaymentsMap = customerInfo.payments.reduce((map, payment) => {
    if (payment.transaction_id) {
      map[payment.transaction_id] = payment;
    }
    return map;
  }, {});

  let stripePayments = customerInfo.invoices ? customerInfo.invoices
    .filter(payment => payment.object === 'invoice')
    .map(payment => {
      // Check if we have a database record for this payment
      const savedPayment = savedPaymentsMap[payment.charge];

      return {
        id: payment.id,
        amount: payment.total,
        date: dayjs.unix(payment.status_transitions.paid_at),
        method: 0,
        // Use database record if available, otherwise determine from Stripe status
        paid: savedPayment ? savedPayment.paid : (payment.status === 'paid' || payment.paid),
        // Use database record for refunded status if available, otherwise use Stripe data
        refunded: savedPayment && savedPayment.refunded ? true : (payment.amount_refunded === payment.total),
        transactionId: payment.charge,
        userId: profileData.id,
        status: payment.status,
        pdf: payment.invoice_pdf,
        // totalPaid: Math.abs(payment.total - payment.amount_refunded),
      };
    }) : [];

  // filter out failed payments that are actually succeeded with another payment
  /* stripePayments = stripePayments
    .filter(payment =>
      payment.status === 'failed'
        ? !stripePayments.find(p => p.invoice && payment.invoice && p.status === 'succeeded' && p.invoice === payment.invoice)
        : true) */

  const stripeOnlyId = stripePayments.map(p => p.charge);

  const otherPayments = customerInfo.payments
    .filter(payment => payment.total !== 0 && !stripeOnlyId.includes(payment.transactionId))
    .map(payment => ({
      ...payment,
      date: dayjs(payment.date, dateFormat),
      totalPaid: payment.amount,
    }));

  stripePayments = stripePayments.map(payment => {
    const savedPayment = savedPaymentsMap[payment.transactionId];

    const totalPaid = savedPayment && !savedPayment.refunded
      ? savedPayment.amount - savedPayment.amount_refunded
      : payment.amount;

    return {
      ...payment,
      totalPaid,
    };
  });

  const payments = [...stripePayments, ...otherPayments];

  if (subscriptionStart) {
    delayedStart = dayjs(subscriptionStart, dateFormat);

    if (!delayedStart.isAfter(dayjs())) {
      delayedStart = null;
    }
  }

  const customer = customerInfo['stripe_customer'];
  let defaultPaymentMethod = customerInfo['default_payment_method'];

  if (isStripeCustomer && customer) {
    let { sources } = customer;
    const { default_source } = customer;

    if (!defaultPaymentMethod && sources && sources.data && sources.data.length) {
      defaultPaymentMethod = sources.data[0];
    }

    if (defaultPaymentMethod) {
      let { exp_month, exp_year } = defaultPaymentMethod;

      card = defaultPaymentMethod.card || default_source;

      if (card) {
        exp_month = card.exp_month;
        exp_year = card.exp_year;
      }

      if (exp_month < 10) exp_month = `0${exp_month}`;

      exp_month += '';
      exp_year += '';

      cardExpDate = dayjs(`${exp_year}-${exp_month}-01`, 'YYYY-MM-DD').endOf('month');
      cardExpDate = cardExpDate.subtract(1, 'month');
      cardExpiring = dayjs().isAfter(cardExpDate);
      cardExpDate = cardExpDate.add(1, 'month');
      cardExpired = !cardExpiring && dayjs().isAfter(cardExpDate);
    }

    subscription = customerInfo.subscription;
    upcomingInvoice = customerInfo.upcoming;

    if (subscription) {
      if (subscription.discount && subscription.discount.coupon) {
        coupon = subscription.discount.coupon;
      } else if (customer.discount && customer.discount.coupon) {
        coupon = customer.discount.coupon;
      }

      if (subscription.plan && plans.find(plan => plan.id === subscription.plan.id)) {
        hasPublicStripePlan = true;
      }


      if (subscription && subscription.plan && subscription.plan.metadata && subscription.plan.metadata.box && subscription.plan.metadata.box === 'true') {
        hasStripeBox = true;
      }

      planInterval = subscription && subscription.plan && subscription.plan.interval;
      planIntervalCount = subscription && subscription.plan && subscription.plan.interval_count;

      const regularPlans = plans.filter(plan => plan && plan.metadata && plan.metadata.box && plan.metadata.box !== 'true');
      const boxPlans = plans.filter(plan => plan && plan.metadata && plan.metadata.box && plan.metadata.box === 'true');

      if (!hasBox && !hasStripeBox) {
        if (hasPublicStripePlan) {
          if (planInterval === 'month') {
            if (planIntervalCount === 1) {
              boxUpgradePlan = boxPlans.find(plan => plan && plan.interval && plan.interval === 'month');
            } else {
              boxUpgradePlan = boxPlans.find(plan => plan && plan.interval && plan.interval === 'year');
            }
          } else {
            boxUpgradePlan = boxPlans.find(plan => plan && plan.interval && plan.interval === 'year');
          }
        }
      }

      if (planInterval === 'month') {
        if (hasBox) {
          intervalChangePlan = boxPlans.find(plan => plan && plan.interval && plan.interval === 'year');
        } else {
          intervalChangePlan = regularPlans.find(plan => plan && plan.interval && plan.interval === 'year');
        }
      } else if (planInterval === 'year') {
        if (hasBox) {
          intervalChangePlan = boxPlans.find(plan => plan && plan.interval && plan.interval === 'month');
        } else {
          intervalChangePlan = regularPlans.find(plan => plan && plan.interval && plan.interval === 'month');
        }
      }

      activeSubscription = subscription.status === 'active' || subscription.status === 'trialing';
      pastDue = subscription.status === 'past_due';

      if (customer.sources && customer.sources.data.length) {
        [ paymentSource ] = customer.sources.data;
      }

      if (activeSubscription) {
        hasCancelled = subscription.cancel_at_period_end === true;
        currentPeriodEnd = dayjs.unix(subscription.current_period_end);
      }

      if (upcomingInvoice) {
        nextPayment = dayjs.unix(upcomingInvoice.next_payment_attempt);
        nextPaymentAmount = upcomingInvoice.total/100;
      }
    }
  } else if (isStrexCustomer && customer) {
    activeSubscription = customer.activeSubscriber && customer.activeSubscriber !== 'false';

    hasCancelled = ['cancelAtPeriodEnd', 'false'].includes(customer.activeSubscriber);

    if (customer.nextPayment) {
      nextPayment = dayjs(customer.nextPayment, dateFormat);
    }

    if (customer.oldNextPayment) {
      oldNextPayment = dayjs(customer.oldNextPayment, dateFormat);
    }
  }

  if (!nextPayment && delayedStart) {
    nextPayment = delayedStart;
  }

  const willCancelImmediately = planInterval === 'month' && planIntervalCount === 1;

  return {
    usesInvoice,
    isStrexCustomer,
    isStripeCustomer,
    subscription,
    pastDue,
    nextPayment,
    oldNextPayment,
    hasCancelled,
    activeSubscription,
    plans,
    strexContracts,
    payments,
    paymentSource,
    delayedStart,
    hasBox,
    hasStripeBox,
    paymentFrequency,
    hasPublicStripePlan,
    cardExpDate,
    cardExpiring,
    cardExpired,
    card,
    coupon,
    profileData,
    subscriptionState,
    upcomingInvoice,
    nextPaymentAmount,
    planInterval,
    planIntervalCount,
    currentPeriodEnd,
    willCancelImmediately,
    name,
    boxUpgradePlan,
    intervalChangePlan,
    routeInfo,
  };
};

export const pickups = (user) => {
  if (!user || !Object.keys(user).length) {
    return {};
  }

  const { delayedStart } = subscriptionData(user);

  let { prev, next } = user.pickup_data;

  if (delayedStart) {
    next = next
      .filter(pickup => {
        const d = dayjs(pickup.date);
        return (d.isAfter(delayedStart)
          || d.isSame(delayedStart)
          || (d.isSame(dayjs(), 'day') && delayedStart.isSame(dayjs().add(1, 'days'), 'day')) // subscription start is tomorrow, but first pickup is today
        );
      });
  }

  for(let i=0; i < next.length; i+=1){
    next[i].date = dayjs(next[i].date);
    next[i].key = `prev_${i}`;
  }

  for(let i=0; i < prev.length; i+=1){
    prev[i].date = dayjs(prev[i].date);
    prev[i].key = `prev_${i}`;
  }

  next = next.sort((a, b) => a.date-b.date);
  prev = prev.sort((a, b) => b.date-a.date);

  return { prevPickups: prev, nextPickups: next };
};